import { useInfiniteQuery } from "@tanstack/react-query";
import { auxiliaryQueryKeys } from "../queryKeys";

export interface SceneSummaryItem {
  id: string;
  sceneId: string;
  summaryText: string;
  generatedAt: string;
}

export interface SceneSummariesResult<TSummary> {
  summaries: ReadonlyArray<TSummary>;
  hasNextPage: boolean;
  isLoading: boolean;
  isLoadingNextPage: boolean;
  error: Error | null;
  loadNextPage(): void;
  retry(): void;
}

export function useSceneSummaries(
  baseUrl: string,
  scenarioId: string,
  pageSize = 20,
): SceneSummariesResult<SceneSummaryItem> {
  const query = useInfiniteQuery<SceneSummariesResponse, Error>({
    queryKey: auxiliaryQueryKeys.sceneSummaries({
      baseUrl,
      scenarioId,
      page: 1,
      pageSize,
    }),
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const url = `${baseUrl}/scenarios/${encodeURIComponent(scenarioId)}/scene-summaries?page=${pageParam}&limit=${pageSize}`;
      const response = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return (await response.json()) as SceneSummariesResponse;
    },
    getNextPageParam: (lastPage, pages) => {
      const loadedCount = pages.reduce(
        (count, page) => count + page.summaries.length,
        0,
      );
      return loadedCount < lastPage.total ? lastPage.page + 1 : undefined;
    },
    retry: false,
  });

  return {
    summaries: query.data?.pages.flatMap(({ summaries }) => summaries) ?? [],
    hasNextPage: query.hasNextPage,
    isLoading: query.isLoading,
    isLoadingNextPage: query.isFetchingNextPage,
    error: query.error,
    loadNextPage: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) {
        void query.fetchNextPage({ cancelRefetch: false });
      }
    },
    retry: () => {
      void query.refetch();
    },
  };
}

interface SceneSummariesResponse {
  summaries: SceneSummaryItem[];
  total: number;
  page: number;
  limit: number;
}
